import React, { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { db } from '../../../firebase';
import { collection, query, getDocs, doc, setDoc, serverTimestamp } from 'firebase/firestore';
import TabelaCRM from '../components/TabelaCRM';
import FormularioDinamico from '../components/FormularioDinamico';

const Obras = () => {
    const { workspaceData } = useOutletContext();
    const [view, setView] = useState('LIST'); // 'LIST' ou 'FORM'
    const [obras, setObras] = useState([]);
    const [empresasClientes, setEmpresasClientes] = useState([]); // Necessário para a 3FN (Chave Estrangeira)
    const [isProcessing, setIsProcessing] = useState(false);
    const [editingObra, setEditingObra] = useState(null);

    // --- FUNÇÕES DE CARREGAMENTO (Isolamento Multitenant) ---
    const carregarDados = async () => {
        if (!workspaceData?.cnpj_completo) return;
        setIsProcessing(true);
        try {
            // 1. Carrega as Obras
            const qObras = query(collection(db, 'workspaces', workspaceData.cnpj_completo, 'crm_obras'));
            const snapObras = await getDocs(qObras);
            const listaObras = snapObras.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setObras(listaObras);

            // 2. Carrega as Empresas para o Dropdown do Formulário (Relação Cliente -> Obra)
            const qEmpresas = query(collection(db, 'workspaces', workspaceData.cnpj_completo, 'crm_empresas'));
            const snapEmpresas = await getDocs(qEmpresas);
            const listaEmpresas = snapEmpresas.docs.map(doc => ({ label: doc.data().nome_fantasia, value: doc.id }));
            setEmpresasClientes(listaEmpresas);
        } catch (error) {
            console.error("Erro ao carregar dados de Obras/Empresas:", error);
            alert("Falha ao carregar a base de dados.");
        } finally {
            setIsProcessing(false);
        }
    };

    useEffect(() => {
        carregarDados();
    }, [workspaceData]);

    // --- DEFINIÇÃO DE COLUNAS (Agnóstico para TabelaCRM) ---
    const colunas = [
        { header: 'Nome da Obra / Projeto', accessor: 'titulo' },
        { header: 'Cliente Vinculado', render: (row) => {
            // Mapeia o ID da empresa para o Nome Fantasia visualmente
            const cliente = empresasClientes.find(e => e.value === row.empresa_id);
            return <strong style={{ color: '#0369a1' }}>{cliente ? cliente.label : 'Cliente Desconhecido'}</strong>;
        }},
        { header: 'Localização', render: (row) => `${row.cidade} - ${row.uf}` },
        { header: 'Vertical', render: (row) => (
            <span style={{ background: '#f1f5f9', color: '#475569', padding: '3px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 'bold' }}>
                {row.vertical_primaria}
            </span>
        )},
        { header: 'Status', render: (row) => {
            let color = '#64748b'; // PARALISADA
            if (row.status === 'EM_ANDAMENTO') color = '#10b981';
            if (row.status === 'CONCLUIDA') color = '#0ea5e9';
            return <span style={{ color: color, fontWeight: 'bold', fontSize: '0.85rem' }}>{row.status.replace('_', ' ')}</span>;
        }}
    ];

    // --- DEFINIÇÃO DE CAMPOS (FormularioDinamico) ---
    // O useMemo garante que os options do 'empresa_id' sejam atualizados assim que a API do Firebase responder
    const camposForm = useMemo(() => [
        { name: 'titulo', label: 'Nome da Obra / Contrato', type: 'text', required: true, colSpan: 12, placeholder: 'Ex: Lote 3 - Duplicação BR-116' },
        { name: 'empresa_id', label: 'Cliente Titular (Construtora/Concessionária)', type: 'select', required: true, colSpan: 12, options: [
            { label: 'Selecione um cliente...', value: '' },
            ...empresasClientes
        ]},
        { name: 'vertical_primaria', label: 'Vertical de Controle (Material Base)', type: 'select', required: true, colSpan: 6, options: [
            { label: 'Asfalto / Pavimentação', value: 'ASFALTO' },
            { label: 'Concreto / Estrutural', value: 'CONCRETO' },
            { label: 'Geotecnia / Solos', value: 'SOLOS' },
            { label: 'Misto / Infraestrutura Geral', value: 'MISTO' }
        ]},
        { name: 'status', label: 'Status da Obra', type: 'select', required: true, colSpan: 6, options: [
            { label: 'Em Andamento', value: 'EM_ANDAMENTO' },
            { label: 'Concluída', value: 'CONCLUIDA' },
            { label: 'Paralisada', value: 'PARALISADA' }
        ]},
        { name: 'divider_loc', label: 'LOCALIZAÇÃO DO ATIVO', type: 'title', colSpan: 12 },
        { name: 'cidade', label: 'Cidade Polo', type: 'text', required: true, colSpan: 8, placeholder: 'Cidade base do trecho' },
        { name: 'uf', label: 'UF', type: 'text', required: true, colSpan: 4, placeholder: 'Ex: SP' },
        { name: 'observacoes', label: 'Observações do Contrato (Opcional)', type: 'textarea', colSpan: 12, placeholder: 'Detalhes sobre faturamento, engenheiro residente, ou particularidades da obra...' }
    ], [empresasClientes]);

    // --- LÓGICA DE ESCRITA NO BANCO (Create / Update) ---
    const handleSaveObra = async (formData) => {
        setIsProcessing(true);
        try {
            // Se for edição, usa o ID existente. Se for nova, o Firebase gera um doc() novo com ID aleatório seguro.
            const obraRef = editingObra?.id 
                ? doc(db, 'workspaces', workspaceData.cnpj_completo, 'crm_obras', editingObra.id)
                : doc(collection(db, 'workspaces', workspaceData.cnpj_completo, 'crm_obras'));

            // Remove a chave de ID de dentro do payload para manter o banco limpo (o ID já é a chave do documento)
            const payload = { ...formData };
            delete payload.id; 

            await setDoc(obraRef, {
                ...payload,
                updated_at: serverTimestamp(),
                created_at: editingObra?.created_at || serverTimestamp()
            }, { merge: true });

            alert(`✅ Obra ${editingObra ? 'atualizada' : 'cadastrada'} com sucesso!`);
            setView('LIST');
            carregarDados(); // Recarrega a tabela atômica
        } catch (e) {
            alert("Erro crítico ao salvar obra: " + e.message);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div style={{ padding: '30px', height: '100%', overflowY: 'auto', background: '#f8fafc' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <div>
                    <h2 style={{ color: '#0f172a', margin: '0 0 5px 0', fontSize: '1.8rem', fontWeight: '800' }}>Gestão de Obras</h2>
                    <p style={{ color: '#64748b', margin: 0, fontSize: '0.95rem' }}>Mapeamento de ativos geográficos e contratos de prestação de serviços.</p>
                </div>
                <button 
                    className="pts-btn-primary" 
                    onClick={() => {
                        setEditingObra(null);
                        setView(view === 'LIST' ? 'FORM' : 'LIST');
                    }}
                    style={{ padding: '10px 20px', fontSize: '0.95rem' }}
                >
                    {view === 'LIST' ? '+ Nova Obra' : '← Voltar para Lista'}
                </button>
            </div>

            {view === 'LIST' ? (
                <div style={{ boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)', borderRadius: '12px' }}>
                    <TabelaCRM 
                        columns={colunas} 
                        data={obras} 
                        onRowClick={(row) => {
                            setEditingObra(row);
                            setView('FORM');
                        }}
                        emptyMessage="Nenhuma obra cadastrada. Clique em '+ Nova Obra' para vincular um projeto a um cliente."
                    />
                </div>
            ) : (
                <div style={{ maxWidth: '800px', margin: '0 auto', background: '#fff', padding: '40px', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.05)' }}>
                    <h3 style={{ borderBottom: '2px solid #f1f5f9', paddingBottom: '15px', marginBottom: '25px', color: '#0369a1' }}>
                        {editingObra ? 'Editar Dados da Obra' : 'Registro de Novo Contrato/Obra'}
                    </h3>
                    <FormularioDinamico 
                        fields={camposForm} 
                        initialData={editingObra}
                        onSubmit={handleSaveObra}
                        onCancel={() => setView('LIST')}
                        isProcessing={isProcessing}
                        submitLabel={editingObra ? "Atualizar Dados da Obra" : "Registrar Obra no Sistema"}
                    />
                </div>
            )}
        </div>
    );
};

export default Obras;